/* eslint-disable @typescript-eslint/no-explicit-any */
import { ExportData } from 'model/reports/export/ExportData';
import _ from 'lodash';
import { ResponseByCountResult } from 'model/reports/ResponseByCountResult';
import { StatisticsResults } from 'model/reports/StatisticsResults';
import { GridResults } from 'model/reports/GridResults';
import { getPercentForGrid, getPercentLabelForGrid } from 'helpers/results';
import { RankResults } from 'model/reports/RanksData';
import PptxGenJS from 'pptxgenjs';
import htmlToPdfMake from 'html-to-pdfmake';

export const exportAsPPTX = async (
  data: ExportData[],
  name: string
): Promise<void> => {
  const pptx = new PptxGenJS();
  pptx.author = 'Eversana';
  pptx.title = `${name} Survey Results`;
  pptx.layout = 'LAYOUT_4x3';

  _.forEach(data, (d, index) => {
    let slide = pptx.addSlide();

    const title = getQuestionTitle(index);
    slide.addText(title.text, title.conf);

    const qLength = getQuestionLength(d.text);
    let isLongQuestionOffcet = qLength === 'md';

    const data = htmlToPdfMake(
      `<p>${d.text}</p>`.replace(new RegExp('\r?\n', 'g'), '<br />')
    );

    let newData = [] as {}[];
    let count = 0;
    let bulletCount = 0;
    const updateArray = (content: any, node?: string) => {
      for (const el of content) {
        if (Array.isArray(el.text)) {
          updateArray(el.text, node);
        } else if (el.text) {
          if (node === 'OL') count = 1 + count;
          if (node === 'UL') bulletCount = 1 + bulletCount;
          if (count > 1 || bulletCount > 1) isLongQuestionOffcet = true;
          newData = [
            ...newData,
            {
              text:
                count !== 0
                  ? `${count === 1 ? '\n' : ''}${count}) ${el.text}\n`
                  : node === 'UL'
                  ? `${bulletCount === 1 ? '\n' : ''}• ${el.text}\n`
                  : el.text,
              options: {
                bold: el.bold ? el.bold : false,
                italic: el.italics ? el.italics : false,
                underline: {
                  style:
                    Array.isArray(el.decoration) &&
                    el.decoration[0] === 'underline'
                      ? 'heavy'
                      : 'none',
                },
                margin: el.margin ? el.margin : [],
              },
            },
          ];
        } else if (!el.text) {
          for (const list in el) {
            if (Array.isArray(el[list]) && el['nodeName'])
              updateArray(el[list], el['nodeName']);
          }
        }
      }
    };

    updateArray(data);

    const text = getQuestionText(d.text, isLongQuestionOffcet);

    slide.addText(newData, text.conf as PptxGenJS.TextPropsOptions);

    if (qLength === 'lg') {
      slide = pptx.addSlide();
    }

    switch (d.type) {
      case 'image': {
        slide.addImage(getChartReportContent(
          d,
          !isLongQuestionOffcet
        ) as PptxGenJS.ImageProps);
        break;
      }
      case 'responseByCountTable': {
        const table = getResponseTable(d, !isLongQuestionOffcet);
        slide.addTable(table.rows as PptxGenJS.TableRow[], table.config);
        break;
      }
      case 'statisticsTable': {
        const table = getStatisticsResponseTable(d, !isLongQuestionOffcet);
        slide.addTable(table.rows as PptxGenJS.TableRow[], table.config);
        break;
      }
      case 'gridTable': {
        const table = getGridTable(d, !isLongQuestionOffcet);
        slide.addTable(table.rows, table.config);
        break;
      }
      case 'RanksTable': {
        const table = getRanksResponseTable(d, !isLongQuestionOffcet);
        slide.addTable(table.rows as PptxGenJS.TableRow[], table.config);
        break;
      }
      default:
        throw Error(
          `Unknown Export Type (${d.type}) was used in export builder.`
        );
    }
  });

  pptx.writeFile({ fileName: `${name} Results`, compression: true });
};

const getQuestionTitle = (qIndex: number) => ({
  text: `Question ${qIndex + 1}`,
  conf: {
    x: 0.5,
    y: 0.5,
    h: 0.5,
    w: 9,
    fontSize: 16,
    bold: true,
    color: '000000',
  },
});
const getQuestionText = (text: string, isLongContent: boolean) => ({
  text: text,
  conf: {
    x: 0.5,
    y: 1,
    h: isLongContent ? 3 : 1.5,
    w: 9,
    valign: 'top',
    fontSize: 14,
    color: '000000',
  },
});

const getChartReportContent = (
  content: ExportData,
  isLongContent: boolean
) => ({
  x: 0.5,
  y: isLongContent ? 2.5 : 4,
  h: isLongContent ? 4.5 : 3,
  w: 9,
  sizing: { type: 'contain' },
  data: content.data as string,
});

const getTableHeaderElement = (text: string) => ({
  text: text,
  options: {
    color: '000000',
    bold: true,
    fontSize: 12,
    align: 'left',
    valign: 'center',
  },
});
const getTableElement = (text: string | number) => ({
  text: text,
  options: {
    color: '000000',
    bold: false,
    fontSize: 12,
    align: 'left',
    valign: 'center',
  },
});
const getTableCenterHeaderElement = (text: string) => ({
  text: text,
  options: {
    color: '000000',
    bold: true,
    fontSize: 12,
    align: 'center',
    valign: 'center',
  },
});
const getTableCenterElement = (text: string | number) => ({
  text: text,
  options: {
    color: '000000',
    bold: false,
    fontSize: 12,
    align: 'center',
    valign: 'center',
  },
});

const getResponseTable = (content: ExportData, isLongContent: boolean) => {
  const report = content.data as ResponseByCountResult;
  return {
    rows: _.concat(
      [[getTableHeaderElement('Count'), getTableHeaderElement('Response')]],
      _.map(report, r => [
        getTableElement(r.count),
        getTableElement(r.response),
      ])
    ),
    config: {
      x: 0.5,
      y: isLongContent ? 2.5 : 4,
      w: 9,
      border: { pt: 1, color: 'D3D3D3' },
    },
  };
};
const getStatisticsResponseTable = (
  content: ExportData,
  isLongContent: boolean
) => {
  const report = content.data as StatisticsResults;
  return {
    rows: _.concat(
      [
        [
          getTableHeaderElement('Item'),
          getTableHeaderElement('Average'),
          getTableHeaderElement('Min'),
          getTableHeaderElement('Max'),
          getTableHeaderElement('Total Responses'),
        ],
      ],
      _.map(report.labels, (l, index) => [
        getTableElement(l),
        getTableElement(report.data[index].avg),
        getTableElement(report.data[index].min),
        getTableElement(report.data[index].max),
        getTableElement(report.data[index].totalResponses),
      ])
    ),
    config: {
      x: 0.5,
      y: isLongContent ? 2.5 : 4,
      w: 9,
      border: { pt: 1, color: 'D3D3D3' },
    },
  };
};

const getGridTable = (content: ExportData, isLongContent: boolean) => {
  const report = content.data as GridResults;
  return {
    rows: _.concat(
      [
        _.concat(
          [getTableCenterHeaderElement('')],
          _.map(report.columnLabels, cl => getTableCenterHeaderElement(cl))
        ),
      ],
      _.map(report.rowLabels, (rl, rowIdx) =>
        _.concat(
          [
            {
              text: [
                getTableElement(
                  rl +
                    `\nCount (${getPercentLabelForGrid(
                      report.requiredStrategy
                    )} %)`
                ),
              ],
            },
          ] as any[],
          _.map(report.columnLabels, (_cl, columnIdx) => {
            const data = _.find(
              report.data,
              d => d.rowIdx === rowIdx && d.columnIdx === columnIdx
            );
            const text = data
              ? `${data.count} (${getPercentForGrid(
                  report.requiredStrategy,
                  data
                )}%)`
              : 'Not found';
            return getTableCenterElement(text);
          })
        )
      )
    ),
    config: {
      x: 0.5,
      y: isLongContent ? 2.5 : 4,
      w: 9,
      border: { pt: 1, color: 'D3D3D3' },
    },
  };
};

const getRanksResponseTable = (content: ExportData, isLongContent: boolean) => {
  const report = content.data as RankResults;
  return {
    rows: _.concat(
      [
        _.concat(
          [
            {
              text: 'Answer Options',
              style: 'header',
              options: {
                bold: true,
                colspan: 3,
                align: 'center',
              },
            },
          ],
          _.map(report.labels, (_, i) => ({
            text: `${i + 1}`,
            style: 'header',
            options: {
              bold: true,
              colspan: 1,
              align: 'center',
            },
          }))
        ),
      ],
      _.map(report.labels, (rl, rowIdx) =>
        _.concat(
          [
            {
              text: [
                {
                  text: rl,
                  style: 'tableElement',
                },
                {
                  text: `\nCount (Column %)`,
                  style: 'subHeader',
                  options: {
                    color: '999999',
                  },
                },
              ],
              options: {
                colspan: 3,
              },
            },
          ] as any[],
          _.map(report.labels, (_cl, columnIdx) => {
            const value = _.find(
              report.data,
              d => d.order === columnIdx && d.index === rowIdx
            );
            const data = value
              ? _.find(
                  report.countData,
                  d =>
                    d.data.index === value.index && d.data.order === value.order
                )
              : null;
            const text = `${data ? data.count : 0} \n(${
              data ? data.percent : 0
            }%)`;
            return {
              text: text,
              style: 'tableElement',
              options: {
                colspan: 1,
                align: 'center',
              },
            };
          })
        )
      )
    ),
    config: {
      x: 0.5,
      y: isLongContent ? 2.25 : 4,
      w: 9,
      border: { pt: 1, color: 'D3D3D3' },
      autoPage: true,
      autoPageRepeatHeader: true,
      verbose: false,
      autoPageLineWeight: 0.1,
      autoPageCharWeight: -0.1,
    },
  };
};

type QuestionLength = 'sm' | 'md' | 'lg';
const getQuestionLength = (text: string): QuestionLength => {
  const length = text.length;
  if (length < 400) {
    return 'sm';
  } else if (length >= 400 && length < 800) {
    return 'md';
  } else {
    return 'lg';
  }
};
